import { useState } from "react";
import { X, FileCode2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { splitHtmlDocument, looksLikeFullHtml } from "@/lib/htmlSplit";

/**
 * PasteHtmlModal — user pastes a full HTML document; we smart-split it into
 * html/css/js and create a new landing page seeded with a single custom_code
 * section. On success we redirect straight into the editor.
 */
export default function PasteHtmlModal({ onClose }) {
  const [title, setTitle] = useState("");
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);

  const preview = raw ? splitHtmlDocument(raw) : null;
  const isFull = looksLikeFullHtml(raw);

  const submit = async () => {
    if (!title.trim()) { toast.error("Page title zaroori hai"); return; }
    if (!raw.trim()) { toast.error("HTML paste karein"); return; }
    setBusy(true);
    try {
      const { html, css, js, head_extras } = splitHtmlDocument(raw);
      const { data: page } = await api.post("/landing-pages", {
        template_id: "blank",
        title: title.trim(),
        kind: "landing",
      });
      await api.patch(`/landing-pages/${page.id}`, {
        sections: [{
          id: "cc" + Math.random().toString(36).slice(2, 8),
          kind: "custom_code",
          html: html || raw,
          css: css || "",
          js: js || "",
          head_extras: head_extras || "",
          height: null,
        }],
      });
      toast.success("Page ban gaya — abhi editor kholte hain");
      window.location.href = `/app/pages/${page.id}/edit`;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Create failed");
      setBusy(false);
    }
  };

  const onEsc = (e) => { if (e.key === "Escape" && !busy) onClose(); };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B0D17]/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      onKeyDown={onEsc}
      data-testid="paste-html-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E4E5E9] px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#0B5CFF]/10 flex items-center justify-center">
              <FileCode2 className="w-5 h-5 text-[#0B5CFF]" />
            </div>
            <div>
              <div className="font-display font-bold text-lg">Paste HTML template</div>
              <div className="text-xs text-[#747680]">Apna full HTML paste karein — hum turant page bana denge</div>
            </div>
          </div>
          <button onClick={onClose} disabled={busy}
            className="p-2 rounded-lg hover:bg-[#F5F5F7] text-[#4A4D57] disabled:opacity-40"
            data-testid="paste-html-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-[#747680]">Page title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Astro Smriti — Consultation"
              className="mt-2 w-full h-11 px-3 rounded-lg border border-[#E4E5E9] focus:border-[#0B5CFF] outline-none"
              data-testid="paste-html-title"
              autoFocus
            />
          </label>

          <label className="block">
            <span className="text-xs font-bold uppercase tracking-widest text-[#747680]">Paste full HTML here</span>
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={`<!doctype html>
<html>
  <head>
    <style>body { font-family: sans-serif; }</style>
  </head>
  <body>
    <h1>Your page…</h1>
    <script>console.log('hi')</script>
  </body>
</html>`}
              className="mt-2 w-full h-64 px-3 py-2 rounded-lg border border-[#E4E5E9] focus:border-[#0B5CFF] outline-none font-mono text-xs leading-relaxed"
              spellCheck="false"
              data-testid="paste-html-textarea"
            />
          </label>

          {preview && (
            <div className="rounded-xl bg-[#F5F5F7] p-3 flex items-center gap-4 flex-wrap text-xs" data-testid="paste-html-preview">
              <Wand2 className="w-4 h-4 text-[#0B5CFF]" />
              <span className="font-semibold text-[#0B0D17]">Smart split:</span>
              <span>HTML <b>{preview.html.length}</b></span>
              <span>CSS <b>{preview.css.length}</b></span>
              <span>JS <b>{preview.js.length}</b></span>
              {preview.head_extras && <span>Head extras <b>{preview.head_extras.length}</b></span>}
              <span className={"ml-auto vp-chip " + (isFull ? "vp-chip-accent" : "")}>
                {isFull ? "Full document detected" : "Fragment — will be used as HTML"}
              </span>
            </div>
          )}

          <div className="text-xs text-[#747680] leading-relaxed">
            <b>Tip:</b> Paste karne ke baad hum automatically <code className="bg-[#F5F5F7] px-1 rounded">&lt;style&gt;</code> aur
            <code className="bg-[#F5F5F7] px-1 rounded"> &lt;script&gt;</code> ko alag kar denge. Baad me kabhi bhi editor ke
            HTML/CSS/JS tabs se change kar sakte hain.
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E4E5E9] px-6 py-4 flex items-center justify-end gap-2 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={busy}
            className="text-sm font-semibold px-4 py-2 rounded-lg text-[#4A4D57] hover:bg-[#F5F5F7] disabled:opacity-40"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !title.trim() || !raw.trim()}
            className="vp-btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="paste-html-create"
          >
            {busy ? "Creating…" : "Create page & open editor"}
          </button>
        </div>
      </div>
    </div>
  );
}
